import { useMutation, useQuery, useQueryClient } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { Topic } from "./useTopics";

interface FollowResponse {
    success: boolean;
    action: "followed" | "unfollowed";
    isFollowing: boolean;
    followerCount: number;
    message: string;
}

interface FollowersResponse {
    followerCount: number;
    isFollowing: boolean;
}

interface UserFollowingTopicsResponse {
    topics: Topic[];
    count: number;
}

const TOPIC_FOLLOW_KEY = "topicFollow";
const USER_FOLLOWING_TOPICS_KEY = "userFollowingTopics";

export const useToggleTopicFollow = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (topicId: number) => {
            const response = await loggedInApiCall.post(`/api/social/topics/${topicId}/follow`);
            return response.data as FollowResponse;
        },
        onSuccess: (followResponse, topicId) => {
            // Update topic followers cache
            queryClient.setQueryData([TOPIC_FOLLOW_KEY, topicId], {
                followerCount: followResponse.followerCount,
                isFollowing: followResponse.isFollowing,
            });

            // Invalidate user following topics
            queryClient.invalidateQueries([USER_FOLLOWING_TOPICS_KEY]);

            // Update topics in all caches
            queryClient.setQueriesData({ queryKey: ["topics"] }, (oldData: any) => {
                if (!oldData) return oldData;

                // Handle both array and hydra format
                if (Array.isArray(oldData)) {
                    return oldData.map((topic: Topic) =>
                        topic.id === topicId ? { ...topic, followerCount: followResponse.followerCount } : topic,
                    );
                }

                // Handle hydra format
                if (oldData["hydra:member"]) {
                    return {
                        ...oldData,
                        "hydra:member": oldData["hydra:member"].map((topic: Topic) =>
                            topic.id === topicId ? { ...topic, followerCount: followResponse.followerCount } : topic,
                        ),
                    };
                }

                return oldData;
            });

            // Update trending topics cache
            queryClient.setQueriesData({ queryKey: ["trendingTopics"] }, (oldData: any) => {
                if (!Array.isArray(oldData)) return oldData;

                return oldData.map((topic: Topic) =>
                    topic.id === topicId ? { ...topic, followerCount: followResponse.followerCount } : topic,
                );
            });
        },
        onError: (error: any) => {
            console.error("Failed to toggle topic follow:", error);
        },
    });
};

export const useGetTopicFollowers = (topicId: number) => {
    return useQuery({
        queryKey: [TOPIC_FOLLOW_KEY, topicId],
        queryFn: async () => {
            const response = await loggedInApiCall.get(`/api/social/topics/${topicId}/followers`);
            return response.data as FollowersResponse;
        },
        enabled: !!topicId,
        staleTime: 1000 * 60 * 2, // 2 minutes
    });
};

export const useGetUserFollowingTopics = () => {
    return useQuery({
        queryKey: [USER_FOLLOWING_TOPICS_KEY],
        queryFn: async () => {
            const response = await loggedInApiCall.get("/api/social/users/following-topics");
            return response.data as UserFollowingTopicsResponse;
        },
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};
